import { useList, useTranslate, type CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import type { PublicationListOut } from '@/api/generated/schemas';
import { SelectFilter } from '@/components/common/SelectFilter';
import type { FilterOption } from '@/components/common/SelectFilter';
import { getFilterValue, type SetFilters } from '@/utils/filters';

type ProductSelectFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
};

/**
 * `product_id[eq]` column filter with publication-title autocomplete.
 *
 * Ledger rows carry a product id, and one publication backs up to two of
 * them (ebook + audio), so each matching publication contributes one option
 * per format rather than a single title.
 */
export const ProductSelectFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
}: ProductSelectFilterProps) => {
  const t = useTranslate();
  const current = getFilterValue<number>(filters, field, 'eq');

  const { selectProps, query } = useSelect<PublicationListOut>({
    resource: 'user_catalog.publications',
    optionLabel: 'title',
    optionValue: 'id',
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  // `useSelect`'s own `defaultValue` hydration looks up publication ids, and
  // the stored filter value is a product id — so the current pick is resolved
  // through the `product_ids` filter instead.
  const { result: currentResult } = useList<PublicationListOut>({
    resource: 'user_catalog.publications',
    filters: [{ field: 'product_ids', operator: 'in', value: [current] }],
    pagination: { pageSize: 1, mode: 'server' },
    queryOptions: { enabled: current != null },
  });

  const byId = new Map<number, PublicationListOut>(
    [...(currentResult?.data ?? []), ...(query.data?.data ?? [])].map(
      (publication) => [publication.id, publication]
    )
  );

  const options: FilterOption<number>[] = [...byId.values()].flatMap(
    (publication) =>
      (['ebook', 'audio'] as const).flatMap((kind) => {
        const productId = publication[kind]?.id;
        return productId == null
          ? []
          : [
              {
                value: productId,
                label: `${publication.title} · ${t(
                  `user_catalog.publications.tabs.${kind}`
                )}`,
              },
            ];
      })
  );

  return (
    <SelectFilter<number>
      field={field}
      options={options}
      filters={filters}
      confirm={confirm}
      clearFilters={clearFilters}
      setFilters={setFilters}
      placeholder={placeholder}
      minWidth={280}
      selectProps={{
        showSearch: true,
        filterOption: false,
        onSearch: selectProps.onSearch,
      }}
    />
  );
};
